import React, { useState, useEffect, useCallback } from "react";
import { ShoppingBag, Loader2, Trash2, Sparkles, Star, ExternalLink, TrendingDown, Bell, BellRing, Check, ShieldCheck, Trophy, ThumbsUp, ThumbsDown } from "lucide-react";
import { shopSearch, shopList, shopDelete, shopWatchAdd, shopWatchList, shopWatchDelete } from "../lib/api";
import { toast } from "sonner";
import { Camera, Plane, X } from "lucide-react";
import { ShopOffers } from "./ShopOffers";
import { TravelPanel } from "./TravelPanel";

const shrink = (file) => new Promise((res, rej) => {
  const img = new Image();
  img.onload = () => {
    const k = Math.min(1, 1280 / Math.max(img.width, img.height));
    const c = document.createElement("canvas"); c.width = img.width * k; c.height = img.height * k;
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    res(c.toDataURL("image/jpeg", 0.85).split(",")[1]);
  };
  img.onerror = rej; img.src = URL.createObjectURL(file);
});

const money = (p, cur) => {
  if (p === null || p === undefined || p === "") return "—";
  const sym = { USD: "$", GBP: "£", EUR: "€", INR: "₹" }[cur] || "";
  return `${sym}${p}${sym ? "" : ` ${cur || ""}`}`.trim();
};

const decisionStyle = (d) => {
  const v = (d || "").toLowerCase();
  if (v === "buy") return "text-emerald-300 border-emerald-500/40 bg-emerald-500/10";
  if (v === "skip") return "text-rose-300 border-rose-500/40 bg-rose-500/10";
  return "text-amber-300 border-amber-500/40 bg-amber-500/10"; // wait
};

const Stars = ({ rating }) => {
  if (rating === null || rating === undefined) return null;
  return (
    <span className="inline-flex items-center gap-1 text-amber-300 text-sm" data-testid="shop-rating">
      <Star className="w-4 h-4 fill-amber-300" /> {rating}
    </span>
  );
};

const JevBadge = ({ jev }) => {
  if (!jev || !jev.decision) return null;
  const pct = jev.decision_confidence != null ? ` · ${Math.round(jev.decision_confidence * 100)}%` : "";
  return (
    <div className={`inline-flex flex-col gap-1 px-3 py-2 rounded-xl border ${decisionStyle(jev.decision)}`} data-testid="shop-jev-verdict">
      <span className="text-[10px] uppercase tracking-wider font-bold opacity-70 flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Jev verdict{pct}</span>
      <span className="text-lg font-bold capitalize leading-none">{jev.decision}</span>
      {jev.value_level && <span className="text-[11px] opacity-80">{jev.value_level}</span>}
    </div>
  );
};

const RetailerRow = ({ r, best }) => {
  const isBest = best && r.name === best.name && r.price === best.price;
  return (
    <a href={r.url || "#"} target="_blank" rel="noreferrer" data-testid="shop-retailer-row"
      className={`flex items-center gap-3 px-3 py-2 rounded-lg border transition-colors ${isBest ? "border-emerald-500/50 bg-emerald-500/10" : "border-[#00F0FF1a] bg-[#0a1020] hover:bg-[#00F0FF0a]"}`}>
      {isBest && <Trophy className="w-4 h-4 text-emerald-300 shrink-0" />}
      <span className="text-cyan-50 text-sm font-medium flex-1 truncate">{r.name}</span>
      {r.in_stock === false && <span className="text-rose-300/70 text-[10px]">out of stock</span>}
      <span className={`text-sm font-bold tabular-nums ${isBest ? "text-emerald-300" : "text-cyan-100"}`}>{money(r.price, r.currency)}</span>
      <ExternalLink className="w-3.5 h-3.5 text-cyan-300/40 shrink-0" />
    </a>
  );
};

const ResultCard = ({ rec, onDelete, onWatch }) => {
  const d = rec.result || {};
  const best = d.best_buy || {};
  return (
    <div className="bg-[#060b18] border border-[#00F0FF22] rounded-2xl p-4 space-y-3" data-testid="shop-result-item">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-cyan-50 font-bold text-base leading-snug">{d.product || rec.query}</p>
          <div className="flex items-center gap-3 mt-1 flex-wrap">
            <Stars rating={d.rating} />
            {d.rating_count && <span className="text-cyan-300/50 text-xs">{d.rating_count} reviews</span>}
          </div>
        </div>
        <button onClick={() => onDelete(rec.id)} data-testid="shop-delete" className="text-cyan-200/40 hover:text-rose-300 shrink-0"><Trash2 className="w-4 h-4" /></button>
      </div>

      <div className="flex items-stretch gap-3 flex-wrap">
        {best.price != null && (
          <div className="flex flex-col justify-center px-4 py-2 rounded-xl border border-emerald-500/40 bg-emerald-500/10">
            <span className="text-[10px] uppercase tracking-wider text-emerald-300/70 font-bold">Best price</span>
            <span className="text-2xl font-bold text-emerald-300 leading-none">{money(best.price, best.currency)}</span>
            <span className="text-[11px] text-emerald-200/70">{best.name}</span>
          </div>
        )}
        <JevBadge jev={rec.jev} />
      </div>

      <ShopOffers d={d} />

      {!(d.offers || []).length && (d.retailers || []).length > 0 && (
        <div className="space-y-1.5" data-testid="shop-retailers">
          {d.retailers.map((r, i) => <RetailerRow key={i} r={r} best={best} />)}
        </div>
      )}

      {d.review_summary && (
        <p className="text-cyan-100/80 text-sm leading-relaxed bg-[#0a1020] border border-[#00F0FF14] rounded-lg p-3">{d.review_summary}</p>
      )}

      {((d.pros || []).length > 0 || (d.cons || []).length > 0) && (
        <div className="grid sm:grid-cols-2 gap-3">
          {(d.pros || []).length > 0 && (
            <div className="space-y-1">
              {d.pros.map((p, i) => <p key={i} className="text-emerald-200/90 text-xs flex items-start gap-1.5"><ThumbsUp className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-400" />{p}</p>)}
            </div>
          )}
          {(d.cons || []).length > 0 && (
            <div className="space-y-1">
              {d.cons.map((c, i) => <p key={i} className="text-rose-200/90 text-xs flex items-start gap-1.5"><ThumbsDown className="w-3.5 h-3.5 mt-0.5 shrink-0 text-rose-400" />{c}</p>)}
            </div>
          )}
        </div>
      )}

      {(d.alternatives || []).length > 0 && (
        <div className="text-cyan-200/70 text-xs space-y-0.5">
          <p className="uppercase tracking-wider text-[10px] text-cyan-300/50 font-bold">Also consider</p>
          {d.alternatives.map((a, i) => <p key={i}>• <b className="text-cyan-100">{a.name}</b> — {a.why}</p>)}
        </div>
      )}

      <div className="flex items-center justify-between pt-1">
        <p className="text-cyan-300/30 text-[10px] flex items-center gap-1"><Sparkles className="w-3 h-3" /> AI price research · verify before buying</p>
        <button onClick={() => onWatch(rec.query || d.product)} data-testid="shop-watch-add"
          className="text-[11px] text-[#00F0FF] border border-[#00F0FF33] rounded-full px-3 py-1 hover:bg-[#00F0FF11] flex items-center gap-1"><Bell className="w-3 h-3" /> Watch price</button>
      </div>
    </div>
  );
};

export default function NexusShop({ session }) {
  const userId = session.user_id;
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);
  const [watches, setWatches] = useState([]);
  const [emailOn, setEmailOn] = useState("");
  const [tab, setTab] = useState("products");
  const [photo, setPhoto] = useState(null);
  useEffect(() => { document.body.classList.add("nx-chat-open"); return () => document.body.classList.remove("nx-chat-open"); }, []);

  const load = useCallback(async () => {
    try {
      const [l, w] = await Promise.all([shopList(userId), shopWatchList(userId)]);
      setItems(l.items || []); setWatches(w.items || []); setEmailOn(w.email || "");
    } catch (e) {}
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  const run = async () => {
    if (!query.trim() && !photo) { toast.error("Type what you want to buy or add a photo"); return; }
    setBusy(true);
    try {
      const r = await shopSearch(userId, query.trim(), photo ? await shrink(photo) : undefined);
      setItems((h) => [r, ...h]); setQuery(""); setPhoto(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't research that right now"); }
    finally { setBusy(false); }
  };

  const del = async (id) => { try { await shopDelete(userId, id); setItems((h) => h.filter((x) => x.id !== id)); } catch (e) {} };

  const addWatch = async (q) => {
    if (!q) return;
    const t = window.prompt(`Watch the price of:\n${q}\n\nOptional target price (number only) — leave blank to alert on any drop:`);
    if (t === null) return;
    const target = t.trim() === "" ? null : parseFloat(t.replace(/[^0-9.]/g, ""));
    try {
      const r = await shopWatchAdd(userId, q, isNaN(target) ? null : target);
      setWatches((h) => [r, ...h.filter((x) => x.query !== q)]);
      toast.success("Watching — I'll email you when the price drops");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't start watching"); }
  };

  const delWatch = async (id) => { try { await shopWatchDelete(userId, id); setWatches((h) => h.filter((x) => x.id !== id)); } catch (e) {} };

  return (
    <div className="h-full overflow-y-auto bg-[#030712] p-4 sm:p-6" data-testid="shop-panel">
      <div className="max-w-3xl mx-auto space-y-5">
        <div>
          <h2 className="text-[#00F0FF] font-bold text-2xl flex items-center gap-2"><ShoppingBag className="w-6 h-6" /> Nexus Shop</h2>
          <p className="text-cyan-200/60 text-sm mt-1">Type any product — I'll compare live prices across retailers, summarize what real buyers say, and give you a clear <b>buy / wait / skip</b> verdict. Powered by live web research{" + Jev."}</p>
        </div>

        <div className="flex gap-2" data-testid="shop-tabs">
          {[["products", ShoppingBag, "Products"], ["travel", Plane, "Flights & tickets"]].map(([id, Ic, label]) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`shop-tab-${id}`}
              className={`px-4 py-2 rounded-full text-sm font-bold flex items-center gap-2 border transition-colors ${tab === id ? "bg-[#00F0FF] text-[#030712] border-[#00F0FF]" : "text-cyan-200/70 border-[#00F0FF33] hover:bg-[#00F0FF11]"}`}>
              <Ic className="w-4 h-4" />{label}
            </button>
          ))}
        </div>

        {tab === "travel" ? <TravelPanel userId={userId} /> : <>
        <div className="bg-[#060b18] border border-[#00F0FF33] rounded-2xl p-3 space-y-3">
          <textarea value={query} onChange={(e) => setQuery(e.target.value)} rows={2}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) run(); }}
            placeholder="e.g. Sony WH-1000XM5 headphones · iPhone 16 Pro 256GB · best robot vacuum under $400 · Dyson V15"
            data-testid="shop-query-input"
            className="w-full bg-[#0a1020] border border-[#00F0FF22] rounded-xl px-3 py-2 text-cyan-50 text-sm outline-none resize-none" />
          {photo && (
            <div className="flex items-center gap-2" data-testid="shop-photo-preview">
              <img src={URL.createObjectURL(photo)} alt="product" className="w-14 h-14 rounded-lg object-cover border border-[#00F0FF33]" />
              <span className="text-xs text-cyan-200/70 flex-1 truncate">{photo.name}</span>
              <button onClick={() => setPhoto(null)} data-testid="shop-photo-remove" className="text-cyan-200/50 hover:text-rose-300"><X className="w-4 h-4" /></button>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
          <label className="px-4 py-2.5 rounded-xl text-sm font-bold text-[#00F0FF] border border-[#00F0FF55] hover:bg-[#00F0FF11] flex items-center gap-2 cursor-pointer" data-testid="shop-photo-btn">
            <Camera className="w-4 h-4" /> Photo
            <input type="file" accept="image/*" className="hidden" data-testid="shop-photo-input" onChange={(e) => { setPhoto(e.target.files?.[0] || null); e.target.value = ""; }} />
          </label>
          <button onClick={run} disabled={busy} data-testid="shop-search-btn"
            className="w-full sm:w-auto px-5 py-2.5 rounded-xl text-sm font-bold text-[#030712] bg-[#00F0FF] hover:bg-cyan-300 disabled:opacity-50 flex items-center justify-center gap-2">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> {photo ? "Identifying & comparing…" : "Comparing prices & reviews…"}</> : <><Sparkles className="w-4 h-4" /> Find best price</>}
          </button>
          </div>
        </div>

        {watches.length > 0 && (
          <div className="bg-[#060b18] border border-[#00F0FF22] rounded-2xl p-3 space-y-2" data-testid="shop-watch-list">
            <p className="text-cyan-300/70 text-[11px] uppercase tracking-wider font-bold flex items-center gap-1.5"><BellRing className="w-3.5 h-3.5" /> Price watch {emailOn && <span className="text-cyan-300/40 normal-case tracking-normal font-normal">· alerts to {emailOn}</span>}</p>
            {watches.map((w) => (
              <div key={w.id} className="flex items-center gap-3 text-sm" data-testid="shop-watch-item">
                <TrendingDown className="w-4 h-4 text-cyan-300/50 shrink-0" />
                <span className="text-cyan-50 flex-1 truncate">{w.query}</span>
                {w.last_price != null && <span className="text-cyan-100 tabular-nums">{money(w.last_price, (w.result?.best_buy || {}).currency)}</span>}
                {w.target_price != null && <span className="text-emerald-300/70 text-xs">target {money(w.target_price, (w.result?.best_buy || {}).currency)}</span>}
                {w.notified_at && <Check className="w-3.5 h-3.5 text-emerald-300" />}
                <button onClick={() => delWatch(w.id)} data-testid="shop-watch-delete" className="text-cyan-200/40 hover:text-rose-300"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}

        {items.length > 0 ? (
          <div className="space-y-3">
            {items.map((r) => <ResultCard key={r.id} rec={r} onDelete={del} onWatch={addWatch} />)}
          </div>
        ) : (
          <div className="text-center py-16 text-cyan-200/40" data-testid="shop-empty">
            <ShoppingBag className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="text-sm">No searches yet. Type a product above and I'll find the best price and the real reviews.</p>
          </div>
        )}
        </>}
      </div>
    </div>
  );
}
